import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import BugReportIcon from "@mui/icons-material/BugReport";
import EditNoteIcon from "@mui/icons-material/EditNote";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import LoginIcon from "@mui/icons-material/Login";
import PeopleIcon from "@mui/icons-material/People";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SpeedIcon from "@mui/icons-material/Speed";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Link,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    styled,
    tableCellClasses,
} from "@mui/material";

import api from "../network/Api";
import { loadUserData } from "../slices/LoginSlice";
import { ReadingsInput } from "../subcomponents/ReadingsInput";

const SESSION_ACCENT = "#8d99ae";
const USER_ACCENT = "#a08cd0";
const BILL_ACCENT = "#d9a441";
const READING_ACCENT = "#4dabb5";
const DEBUG_ACCENT = "#e07a5f";
const LINK_COLOR = "#90caf9";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: "#181924",
        color: theme.palette.common.white,
        textAlign: "center",
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
        textAlign: "center",
        color: theme.palette.common.white,
        padding: "8px 6px",
        wordBreak: "break-word",
    },
}));

const UserTableRow = styled(TableRow)({
    "&:nth-of-type(odd)": {
        backgroundColor: "#3b3550",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
});

const BillTableRow = styled(TableRow)({
    "&:nth-of-type(odd)": {
        backgroundColor: "#3f3a48",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
});

const ReadingTableRow = styled(TableRow)({
    "&:nth-of-type(odd)": {
        backgroundColor: "#343d56",
    },
    // hide last border
    "&:last-child td, &:last-child th": {
        border: 0,
    },
});

const formatAmount = (amount) => `${new Intl.NumberFormat("hu").format(Math.round(amount))} Ft`;

const formatTime = (time) => new Date(Date.parse(time)).toLocaleString("hu");

const getLatestReadingTime = (meters) => {
    const times = meters
        .filter((meter) => meter.latest_reading)
        .map((meter) => meter.latest_reading.time);
    return times.length ? times.sort().slice(-1)[0] : null;
};

const getLatestReadingValues = (meters) => {
    let values = {};
    meters.forEach((meter) => {
        if (meter.latest_reading) {
            values[meter.meter_id] = meter.latest_reading.reading_value;
        }
    });
    return values;
};

export const AdminScreen = () => {
    const [apartments, setApartments] = useState(null);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [debugMode, setDebugMode] = useState(false);
    const [unauthorized, setUnauthorized] = useState(false);

    const loadApartments = useCallback(() => {
        api.get("/admin/latest_readings").then((response) => {
            if (!response) {
                console.log("Retrieving the latest readings returned an error");
                setUnauthorized(true);
                return;
            }
            console.log("Received latest readings:", response.data);
            setApartments(response.data);
        });
    }, []);

    const loadSessionInfo = useCallback(() => {
        api.get("/admin/session").then((response) => {
            if (!response) {
                console.log("Retrieving the session info returned an error");
                return;
            }
            console.log("Received session info:", response.data);
            setSessionInfo(response.data);
        });
    }, []);

    useEffect(() => {
        loadApartments();
        loadSessionInfo();
    }, [loadApartments, loadSessionInfo]);

    if (unauthorized) {
        return (
            <Typography>
                Az admin oldal megtekintéséhez admin jogosultsággal rendelkező
                címmel kell bejelentkezni.
            </Typography>
        );
    }

    if (apartments === null) {
        return <Typography>Betöltés...</Typography>;
    }

    return (
        <div>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingBottom: "0.5em",
                }}
            >
                <Typography variant="h6">Lakások áttekintése</Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={debugMode}
                            onChange={(e) => setDebugMode(e.target.checked)}
                        />
                    }
                    label={
                        <Typography sx={{ fontSize: "0.85rem" }}>
                            Debug mód
                        </Typography>
                    }
                />
            </Box>
            <SessionSection
                sessionInfo={sessionInfo}
                debugMode={debugMode}
                refreshHandler={loadSessionInfo}
            />
            {apartments.map((apartment) => (
                <ApartmentAccordion
                    key={apartment.apartment_id}
                    apartment={apartment}
                    debugMode={debugMode}
                    refreshHandler={loadApartments}
                />
            ))}
        </div>
    );
};

const SessionSection = ({ sessionInfo, debugMode, refreshHandler }) => (
    <Section
        title="Munkamenet"
        icon={<BugReportIcon sx={{ color: SESSION_ACCENT }} />}
        accent={SESSION_ACCENT}
    >
        <Box sx={{ padding: "0 0.75em 0.75em" }}>
            {debugMode ? (
                <>
                    <SessionDetail
                        value={sessionInfo && sessionInfo.session}
                    />
                    <SessionDetail
                        value={sessionInfo && sessionInfo.user_details}
                    />
                    <Button
                        size="small"
                        variant="contained"
                        sx={{ marginTop: "0.75em" }}
                        onClick={refreshHandler}
                    >
                        Frissítés
                    </Button>
                </>
            ) : (
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        fontStyle: "italic",
                        color: "rgba(191, 199, 213, 0.6)",
                    }}
                >
                    A munkamenet adatai debug módban láthatóak.
                </Typography>
            )}
        </Box>
    </Section>
);

const SessionDetail = ({ value }) => (
    <Typography
        sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            wordBreak: "break-all",
            paddingBottom: "0.5em",
        }}
    >
        {value || "-"}
    </Typography>
);

const ApartmentAccordion = ({ apartment, debugMode, refreshHandler }) => {
    const latestReadingTime = getLatestReadingTime(apartment.meters);

    return (
        <Accordion
            disableGutters
            sx={{
                backgroundColor: "#292D3E",
                marginBottom: "0.5em",
                "&:before": { display: "none" },
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#BFC7D5" }} />}
                sx={{ backgroundColor: "#181924" }}
            >
                <Typography sx={{ flexGrow: 1 }}>
                    {apartment.address}
                </Typography>
                <Typography
                    sx={{
                        fontSize: "0.75rem",
                        color: "rgba(191, 199, 213, 0.6)",
                        paddingRight: "0.5em",
                        alignSelf: "center",
                    }}
                >
                    {latestReadingTime
                        ? formatTime(latestReadingTime)
                        : "Nincs leolvasás"}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: "0.75em" }}>
                <Section
                    title="Lakók"
                    icon={<PeopleIcon sx={{ color: USER_ACCENT }} />}
                    accent={USER_ACCENT}
                >
                    <UsersTable
                        users={apartment.users}
                        refreshHandler={refreshHandler}
                    />
                </Section>
                <Section
                    title={
                        apartment.latest_bill
                            ? `Legutóbbi számla: ${formatTime(
                                  apartment.latest_bill.time
                              )}`
                            : "Legutóbbi számla"
                    }
                    icon={<ReceiptLongIcon sx={{ color: BILL_ACCENT }} />}
                    accent={BILL_ACCENT}
                >
                    <LatestBillTable bill={apartment.latest_bill} />
                </Section>
                <Section
                    title="Mérőóra állások"
                    icon={<SpeedIcon sx={{ color: READING_ACCENT }} />}
                    accent={READING_ACCENT}
                >
                    <LatestReadingsTable meters={apartment.meters} />
                </Section>
                {debugMode && (
                    <Section
                        title="Leolvasás rögzítése (debug)"
                        icon={<EditNoteIcon sx={{ color: DEBUG_ACCENT }} />}
                        accent={DEBUG_ACCENT}
                    >
                        <ReadingInjector
                            apartment={apartment}
                            refreshHandler={refreshHandler}
                        />
                    </Section>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const Section = ({ title, icon, accent, children }) => (
    <Box
        sx={{
            borderLeft: `4px solid ${accent}`,
            backgroundColor: "#23273a",
            marginBottom: "1.25em",
            "&:last-child": { marginBottom: 0 },
        }}
    >
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5em",
                padding: "0.5em 0.75em",
            }}
        >
            {icon}
            <Typography
                sx={{
                    color: accent,
                    fontSize: "0.9rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                }}
            >
                {title}
            </Typography>
        </Box>
        {/* Wide tables scroll inside the section instead of stretching the page. */}
        <Box sx={{ overflowX: "auto" }}>{children}</Box>
    </Box>
);

const UsersTable = ({ users, refreshHandler }) => {
    const [error, setError] = useState(null);
    const [userToDetach, setUserToDetach] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const detachApartment = () => {
        console.log("Removing the apartment of:", userToDetach.username);
        setError(null);
        api.delete(
            `/admin/users/${encodeURIComponent(userToDetach.username)}/apartment`
        ).then((response) => {
            setUserToDetach(null);
            if (!response) {
                console.log("Removing the apartment returned an error");
                setError("A lakás leválasztása nem sikerült.");
                return;
            }
            refreshHandler();
        });
    };

    const impersonate = (username) => {
        console.log("Logging in as:", username);
        setError(null);
        api.put("/admin/impersonate", { username }).then((response) => {
            if (!response) {
                console.log("Logging in as the user returned an error");
                setError(
                    "A belépés nem sikerült. Csak a beállított admin címekkel " +
                        "bejelentkezve lehet más nevében belépni."
                );
                return;
            }
            dispatch(loadUserData());
            navigate("/");
        });
    };

    if (!users.length) {
        return (
            <Typography sx={{ padding: "0 0.75em 0.75em", fontSize: 14 }}>
                Nincs lakó társítva
            </Typography>
        );
    }

    return (
        <>
            {error && (
                <Typography
                    sx={{
                        padding: "0 0.75em 0.75em",
                        fontSize: 14,
                        color: DEBUG_ACCENT,
                    }}
                >
                    {error}
                </Typography>
            )}
        <Table sx={{ minWidth: 280 }}>
            <TableBody>
                {users.map((user) => (
                    <UserTableRow key={user.username}>
                        <StyledTableCell>{user.name || "-"}</StyledTableCell>
                        <StyledTableCell>
                            <Link
                                href={`mailto:${user.email}`}
                                sx={{
                                    color: LINK_COLOR,
                                    textDecorationColor: LINK_COLOR,
                                }}
                            >
                                {user.email}
                            </Link>
                        </StyledTableCell>
                        <StyledTableCell>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "0.4em",
                                    justifyContent: "center",
                                }}
                            >
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<LoginIcon />}
                                    onClick={() => impersonate(user.username)}
                                >
                                    Belépés
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<LinkOffIcon />}
                                    onClick={() => setUserToDetach(user)}
                                >
                                    Leválasztás
                                </Button>
                            </Box>
                        </StyledTableCell>
                    </UserTableRow>
                ))}
            </TableBody>
        </Table>
        <ConfirmationDialog
            open={Boolean(userToDetach)}
            title="Lakás leválasztása"
            confirmHandler={detachApartment}
            closeHandler={() => setUserToDetach(null)}
        >
            <Typography sx={{ fontSize: 14 }}>
                Biztosan leválasztja a lakást a következő felhasználóról:{" "}
                <b>{userToDetach && (userToDetach.name || userToDetach.email)}</b>?
                A felhasználó ezután újra kiválaszthatja a lakását.
            </Typography>
        </ConfirmationDialog>
        </>
    );
};

const ConfirmationDialog = ({
    open,
    title,
    confirmHandler,
    closeHandler,
    children,
}) => (
    <Dialog
        open={open}
        onClose={closeHandler}
        PaperProps={{ sx: { backgroundColor: "#292D3E" } }}
    >
        <DialogTitle sx={{ color: DEBUG_ACCENT }}>{title}</DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogActions>
            <Button onClick={closeHandler}>Mégse</Button>
            <Button variant="contained" onClick={confirmHandler}>
                Igen
            </Button>
        </DialogActions>
    </Dialog>
);

const LatestBillTable = ({ bill }) => {
    if (!bill) {
        return (
            <Typography sx={{ padding: "0 0.75em 0.75em", fontSize: 14 }}>
                Nincs még számla
            </Typography>
        );
    }

    return (
        <Table sx={{ minWidth: 280 }}>
            <TableBody>
                {bill.components.map(([name, amount]) => (
                    <BillTableRow key={name}>
                        <StyledTableCell>{name}</StyledTableCell>
                        <StyledTableCell>{formatAmount(amount)}</StyledTableCell>
                    </BillTableRow>
                ))}
                <BillTableRow>
                    <StyledTableCell
                        sx={{ fontWeight: "bold", color: BILL_ACCENT }}
                    >
                        Összesen
                    </StyledTableCell>
                    <StyledTableCell
                        sx={{ fontWeight: "bold", color: BILL_ACCENT }}
                    >
                        {formatAmount(bill.amount)}
                    </StyledTableCell>
                </BillTableRow>
            </TableBody>
        </Table>
    );
};

const LatestReadingsTable = ({ meters }) => (
    <Table sx={{ minWidth: 280 }}>
        <TableHead>
            <TableRow>
                <StyledTableCell>Mérőóra:</StyledTableCell>
                <StyledTableCell>Állás:</StyledTableCell>
                <StyledTableCell>Dátum:</StyledTableCell>
                <StyledTableCell>Kép:</StyledTableCell>
            </TableRow>
        </TableHead>
        <TableBody>
            {meters.map((meter) => (
                <ReadingTableRow key={meter.meter_id}>
                    <StyledTableCell>
                        {meter.meter_type_name}
                        <br />
                        {meter.meter_number}
                    </StyledTableCell>
                    <StyledTableCell>
                        {meter.latest_reading
                            ? new Intl.NumberFormat("hu").format(
                                  meter.latest_reading.reading_value
                              )
                            : "Nincs leolvasás"}
                    </StyledTableCell>
                    <StyledTableCell>
                        {meter.latest_reading
                            ? formatTime(meter.latest_reading.time)
                            : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                        {meter.latest_reading &&
                        meter.latest_reading.picture_url ? (
                            <Link
                                href={meter.latest_reading.picture_url}
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    color: LINK_COLOR,
                                    textDecorationColor: LINK_COLOR,
                                }}
                            >
                                Megnézem
                            </Link>
                        ) : (
                            "-"
                        )}
                    </StyledTableCell>
                </ReadingTableRow>
            ))}
        </TableBody>
    </Table>
);

const ReadingInjector = ({ apartment, refreshHandler }) => {
    const [readings, setReadings] = useState([]);
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    // The readings are saved through the debug endpoint, so no bill is generated
    // and no image is uploaded for them.
    const saveReadings = () => {
        let readingsToSave = readings.filter(Boolean);
        console.log("Saving debug readings:", readingsToSave);
        api.post("/admin/readings", readingsToSave).then((response) => {
            if (!response) {
                console.log("Saving the readings returned an error");
                return;
            }
            setConfirmationOpen(false);
            setReadings([]);
            refreshHandler();
        });
    };

    return (
        <Box sx={{ padding: "0 0.75em 0.75em" }}>
            <ReadingsInput
                readings={readings}
                setReadings={setReadings}
                apartment={apartment.apartment_config}
                previousReadings={getLatestReadingValues(apartment.meters)}
                saveButtonHandler={() => setConfirmationOpen(true)}
                onMeterImageChangeHandler={() => () => {}}
                images={{}}
                uploadUrls={{ get: {}, put: {} }}
                skipSavingCheck={true}
            />
            <ReadingInjectorConfirmation
                open={confirmationOpen}
                closeHandler={() => setConfirmationOpen(false)}
                saveHandler={saveReadings}
                apartment={apartment}
                readings={readings}
            />
        </Box>
    );
};

const ReadingInjectorConfirmation = ({
    open,
    closeHandler,
    saveHandler,
    apartment,
    readings,
}) => {
    const getMeterNumber = (meterId) => {
        let meter = apartment.meters.find((item) => item.meter_id === meterId);
        return meter ? meter.meter_number : meterId;
    };

    return (
        <Dialog
            open={open}
            onClose={closeHandler}
            PaperProps={{ sx: { backgroundColor: "#292D3E" } }}
        >
            <DialogTitle sx={{ color: DEBUG_ACCENT }}>
                Leolvasás rögzítése
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: 14 }}>
                    A következő értékek kerülnek mentésre a(z){" "}
                    <b>{apartment.address}</b> címen, számla készítése nélkül:
                </Typography>
                <Table sx={{ marginTop: "0.5em" }}>
                    <TableBody>
                        {readings.filter(Boolean).map((reading) => (
                            <ReadingTableRow key={reading.meter_id}>
                                <StyledTableCell>
                                    {getMeterNumber(reading.meter_id)}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {reading.reading_value}
                                </StyledTableCell>
                            </ReadingTableRow>
                        ))}
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={closeHandler}>Mégse</Button>
                <Button variant="contained" onClick={saveHandler}>
                    Mentés
                </Button>
            </DialogActions>
        </Dialog>
    );
};
